import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCheck, Settings2, Inbox, ClipboardCheck, DollarSign, ListChecks, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import PushToggle from "@/components/PushToggle";
import NotificationPrefs from "@/components/NotificationPrefs";
import TeamCelebrationsToggle from "@/components/TeamCelebrationsToggle";
import NotifRow from "@/components/notifications/NotifRow";
import NotifPager from "@/components/notifications/NotifPager";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/lib/featureFlags";
import { notifLink, requestTag, refreshBadges } from "@/lib/notifications";

const PER_PAGE = 10;
const CHIPS = [
  { key: "all", label: "All", icon: Inbox },
  { key: "requests", label: "Requests", icon: ClipboardCheck },
  { key: "payments", label: "Payments", icon: DollarSign },
  { key: "choices", label: "Choices", icon: ListChecks },
];

function FilterChips({ value, counts, onChange }) {
  return (
    <div className="mb-3 flex flex-wrap gap-2" role="tablist" data-testid="notifications-filter-chips">
      {CHIPS.map(({ key, label, icon: Icon }) => {
        const on = value === key;
        return (
          <button key={key} type="button" role="tab" aria-selected={on} onClick={() => onChange(key)} data-testid={`notifications-filter-${key}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Icon className="h-3.5 w-3.5" /> {label} <span className={`tabular-nums ${on ? "text-white/70" : "text-slate-400"}`}>{counts?.[key] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

// Notifications page = the Archive (read notifications, newest first). Unread ones live on the dashboard card.
export default function NotificationsPage() {
  const { user } = useAuth();
  const { animations: animationsOn } = useFeatureFlags();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const [page, setPage] = useState(() => Number(params.get("page")) || 1);
  const [focusPending, setFocusPending] = useState(!!focus);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [kw, setKw] = useState("");
  const [data, setData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const next = search.trim();
    if (next === kw) return undefined;
    const t = setTimeout(() => { setKw(next); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search, kw]);

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications/paged", {
      params: { page, per_page: PER_PAGE, only_read: true, ...(cat !== "all" ? { category: cat } : {}), ...(kw ? { q: kw } : {}), ...(focusPending ? { focus } : {}) } });
    setData(data);
    if (data.page !== page) setPage(data.page);
    if (focusPending) setFocusPending(false);
  }, [page, cat, kw, focus, focusPending]);
  useEffect(() => { load(); }, [load]);
  const pickCat = (k) => { setCat(k); setPage(1); };

  const markAll = async () => { await api.post("/notifications/read-all"); refreshBadges(); load(); };
  const openNotif = (n, to = notifLink(n, user?.role)) => { if (to) navigate(to); };

  const showPush = user?.role !== "team_member";
  const showCeleb = user?.role === "coach" && animationsOn;
  const hasSettings = showPush || showCeleb;

  return (
    <div>
      <PageHeader title="Notifications" subtitle={!data ? "Loading…" : `Archive · ${data.read} read notification${data.read === 1 ? "" : "s"}, newest first`}
        actions={(
          <div className="flex items-center gap-2">
            {hasSettings && <Button onClick={() => setShowSettings((s) => !s)} variant="outline" className="rounded-full" data-testid="notifications-settings-btn"><Settings2 className="mr-2 h-4 w-4" /> Settings</Button>}
            {data?.unread > 0 && <Button onClick={markAll} variant="outline" className="rounded-full" data-testid="notifications-mark-all"><CheckCheck className="mr-2 h-4 w-4" /> Mark all read ({data.unread})</Button>}
          </div>
        )} />
      {showSettings && hasSettings && (
        <div className="mb-2" data-testid="notifications-settings-panel">
          {showPush && <PushToggle />}
          {showCeleb && <TeamCelebrationsToggle />}
        </div>
      )}
      {(user?.role === "administrator" || user?.role === "parent") && <NotificationPrefs />}
      {data && data.read > 0 && (
        <div className="mb-1">
          <div className="relative mb-3 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the archive…" aria-label="Search the archive"
              className="h-10 rounded-full pl-9 pr-9" data-testid="notifications-search-input" />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label="Clear search" data-testid="notifications-search-clear"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <FilterChips value={cat} counts={data.counts} onChange={pickCat} />
        </div>
      )}
      {!data ? null : data.items.length === 0 ? (
        kw
          ? <EmptyState title={`No matches for "${kw}"`} description={cat === "all" ? "Try a different word." : "Try a different word or the All chip."} testId="notifications-search-empty" />
          : cat === "all"
          ? <EmptyState title="No archived notifications" description="Notifications you read or swipe away on your dashboard will show up here." testId="notifications-empty" />
          : <EmptyState title={`No ${CHIPS.find((c) => c.key === cat).label.toLowerCase()} in the archive`} description="Try another filter or All." testId="notifications-filter-empty" />
      ) : (
        <>
          <div className="space-y-2" data-testid="notifications-archive-list">
            {data.items.map((n) => <NotifRow key={n.id} n={n} tag={requestTag(n, user?.role)} highlight={n.id === focus} hit={kw} onOpen={openNotif} onRead={() => {}} />)}
          </div>
          <div className="mt-4">
            <NotifPager page={data.page} pages={data.pages} onPage={setPage} label={`Archive · ${data.page} / ${data.pages}`} />
          </div>
        </>
      )}
    </div>
  );
}
